import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useCommunication } from './CommunicationWrapper';
import { useCourses } from './courses';

export interface AssignmentType {
	value: string;
	label: string;
	isCustom: boolean;
	isCanvasGroup?: boolean;
}

export interface CourseTypeGroup {
	courseId: string;
	courseName: string;
	types: AssignmentType[];
}

interface AssignmentTypesContextType {
	typesByCourse: CourseTypeGroup[];
	types: AssignmentType[];
	typeLabels: Record<string, string>;
	isLoading: boolean;
	error: string | null;
}

const AssignmentTypesContext = createContext<AssignmentTypesContextType | undefined>(undefined);

function capitalizeWords(str: string): string {
	return str
		.split(' ')
		.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
}

export function AssignmentTypesProvider({ children }: { children: React.ReactNode }) {
	const { sendMessageAndListen, useRerenderListener } = useCommunication();
	const { orderedCourseIds, courseData } = useCourses();
	const { options } = useRerenderListener(["course_custom_types"]);

	const [typesByCourse, setTypesByCourse] = useState<CourseTypeGroup[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!orderedCourseIds || orderedCourseIds.length === 0) {
			setTypesByCourse([]);
			setIsLoading(false);
			return;
		}

		const fetchAllTypes = async () => {
			setIsLoading(true);
			setError(null);

			try {
				const response = await sendMessageAndListen({
					type: "CALL_EXTENSION_FUNC",
					payload: {
						func: "getAssignmentGroupsForCourses",
						args: { courseIds: orderedCourseIds }
					}
				});

				if (!response?.success) {
					throw new Error(response?.error || "Failed to fetch assignment groups");
				}

				const allGroups = response.data || {};
				const courseCustomTypes = options?.course_custom_types || {};

				const groupedTypes: CourseTypeGroup[] = orderedCourseIds
					.map((courseId: string) => {
						const course = courseData?.[courseId];
						const courseName = course?.code || course?.default || courseId;
						const groups = allGroups[courseId] || [];
						const customTypes = courseCustomTypes[courseId] || [];

						const typesMap = new Map<string, AssignmentType>();

						if (Array.isArray(groups)) {
							groups.forEach((group: any) => {
								if (group?.name) {
									const baseValue = group.name.toLowerCase().replace(/\s+/g, '_');
									const value = `${courseId}:${baseValue}`;
									if (!typesMap.has(value)) {
										typesMap.set(value, {
											value,
											label: group.name,
											isCustom: false,
											isCanvasGroup: true,
										});
									}
								}
							});
						}

						if (Array.isArray(customTypes)) {
							customTypes.forEach((typeName: string) => {
								const baseValue = typeName.toLowerCase();
								const value = `${courseId}:${baseValue}`;
								if (!typesMap.has(value)) {
									typesMap.set(value, {
										value,
										label: capitalizeWords(typeName),
										isCustom: true,
										isCanvasGroup: false,
									});
								}
							});
						}

						const sortedTypes = Array.from(typesMap.values()).sort((a, b) =>
							a.label.localeCompare(b.label)
						);

						return {
							courseId,
							courseName,
							types: sortedTypes,
						};
					})
					.filter((group: CourseTypeGroup) => group.types.length > 0);

				setTypesByCourse(groupedTypes);
			} catch (err) {
				console.error('Failed to fetch assignment types:', err);
				setError('Failed to load assignment types');
				setTypesByCourse([]);
			} finally {
				setIsLoading(false);
			}
		};

		fetchAllTypes();
	}, [orderedCourseIds, courseData, options?.course_custom_types, sendMessageAndListen]);

	const typeLabels = useMemo(() => {
		const labels: Record<string, string> = {};
		typesByCourse.forEach(group => {
			group.types.forEach(type => {
				labels[type.value] = type.label;
			});
		});
		return labels;
	}, [typesByCourse]);

	const allTypes = useMemo(() => {
		const uniqueTypes = new Map<string, AssignmentType>();
		typesByCourse.forEach(group => {
			group.types.forEach(type => {
				if (!uniqueTypes.has(type.value)) {
					uniqueTypes.set(type.value, type);
				}
			});
		});
		return Array.from(uniqueTypes.values()).sort((a, b) => a.label.localeCompare(b.label));
	}, [typesByCourse]);

	const value = {
		typesByCourse,
		types: allTypes,
		typeLabels,
		isLoading,
		error,
	};

	return (
		<AssignmentTypesContext.Provider value={value}>
			{children}
		</AssignmentTypesContext.Provider>
	);
}

export function useAssignmentTypes() {
	const context = useContext(AssignmentTypesContext);
	if (context === undefined) {
		throw new Error('useAssignmentTypes must be used within AssignmentTypesProvider');
	}
	return context;
}
